<div class="assign-dialog">
  <!-- header -->
  <div class="assign-header">
    <div class="head-left">
      <span class="eyebrow">Booking confirmation</span>
      <h2 class="dialog-title">Assign accommodation</h2>
      <p class="dialog-sub">Review the guest roster, then assign rooms/beds (full or partial).</p>
    </div>
    <button type="button" class="close-btn" (click)="close()" aria-label="Close">✕</button>
  </div>

  <!-- summary cards -->
  <div class="summary-row">
    <div class="surface-card booking-card">
      <h3 class="card-heading">{{ booking?.bookingType || 'Booking' }} #{{ booking?.bookingId || data.bookingId }}</h3>
      <div class="kv-grid">
        <div class="kv"><span class="kv-label">Family code</span><span class="kv-value">{{ booking?.familyCode || data.familyCode }}</span></div>
        <div class="kv"><span class="kv-label">Total guests</span><span class="kv-value">{{ totalGuests }} <em>{{ femaleCount }}F · {{ maleCount }}M · {{ childCount }}C</em></span></div>
        <div class="kv"><span class="kv-label">Stay</span><span class="kv-value">{{ booking?.fromDate || '—' }} – {{ booking?.toDate || '—' }}</span></div>
        <div class="kv"><span class="kv-label">Accommodation</span><span class="kv-value">{{ booking?.accommodationType || '—' }}</span></div>
        @if(booking?.isAccompaniedByOnly){
        <div class="kv"><span class="kv-label">Accompanied by only · guest contact</span><span class="kv-value">{{ booking?.guestRequestorContact || '—' }}</span></div>
        }
      </div>
    </div>

    <div class="surface-card live-card">
      <div class="live-head">
        <span class="eyebrow">Live summary</span>
        <span class="pill">{{ assignedSummary.properties }} properties · {{ assignedSummary.rooms }} rooms</span>
      </div>
      <h3 class="card-heading">Properties assigned</h3>
      @if(assignedGroups.length){
      <div class="assigned-list">
        @for(group of assignedGroups; track group.propertyId){
        <div class="assigned-group">
          <span class="assigned-property">{{ group.propertyName }}</span>
          @for(room of group.rooms; track room.roomId){
          <div class="assigned-room">
            <div>
              <span class="room-number">Room {{ room.roomNumber }}</span>
              <span class="room-meta">{{ room.full ? 'Full' : 'Partial' }} · {{ room.beds.length }}/{{ room.capacity }} beds</span>
            </div>
            <button type="button" class="link-danger" (click)="release(room.roomId)">Release</button>
          </div>
          }
        </div>
        }
      </div>
      } @else {
      <div class="empty-assigned">
        <span class="empty-icon">▦</span>
        <span class="empty-title">No properties assigned yet</span>
        <span class="empty-sub">Choose a property, pick beds and place guests using Full or Partial capacity.</span>
      </div>
      }
    </div>
  </div>

  <!-- roster + selection -->
  <div class="assign-body">
    <!-- roster -->
    <aside class="surface-card roster">
      <span class="eyebrow">Read-only roster</span>
      <h3 class="card-heading">Guest details</h3>
      <p class="card-note">Guest selection is automatic; beds place each guest as you assign rooms.</p>

      <div class="roster-section">
        <span class="roster-section-title">Female <b>{{ femaleCount }}</b></span>
        <span class="roster-section-title">Male <b>{{ maleCount }}</b></span>
        <span class="roster-section-title">Children <b>{{ childCount }}</b></span>
      </div>

      <ul class="roster-list">
        @for(g of guests; track $index){
        <li class="guest-card">
          <div class="guest-id">
            <span class="guest-name">{{ guestName(g) }}</span>
            <span class="guest-kind">{{ guestKind(g) }}<span *ngIf="g.aadhaarNumber"> · {{ g.aadhaarNumber }}</span></span>
          </div>
          <span class="guest-status" [class.placed]="guestPlaced($index)">{{ guestPlaced($index) ? 'Placed' : 'Waiting for placement' }}</span>
        </li>
        } @empty { <li class="muted">No guest details.</li> }
      </ul>
    </aside>

    <!-- property selection -->
    <section class="surface-card selection">
      <div class="selection-head">
        <div>
          <span class="eyebrow">Accommodation</span>
          <h3 class="card-heading">Property selection</h3>
        </div>
        <span class="pill">{{ placedBeds }} beds assigned</span>
      </div>

      <!-- guest placement progress -->
      <div class="progress-block">
        <div class="progress-line">
          <span>
            {{ placedBeds }} of {{ totalGuests }} guests placed
            @if(selectedBeds){ <span class="progress-selected">+ {{ selectedBeds }} selected</span> }
          </span>
          <span class="progress-pct" [class.ok]="placementPercent >= 100">{{ placementPercent }}%</span>
        </div>
        <div class="progress-bar">
          <div class="progress-fill" [style.width.%]="placedPercent"></div>
          <div class="progress-fill pending" [style.width.%]="placementPercent - placedPercent"></div>
        </div>
        <span class="progress-hint">{{ remainingGuests }} remaining{{ selectedBeds ? ' · click Place to save the selected beds' : (remainingGuests ? ' · a partial assignment can still be confirmed' : '') }}</span>
      </div>

      <label class="field-label" for="assign-property-search">Search properties or rooms</label>
      <div class="search-wrap">
        <span class="search-icon">⌕</span>
        <input id="assign-property-search" class="search-input" placeholder="Search hotel, room or location" [(ngModel)]="propertySearch" />
      </div>

      <div class="count-line">{{ filteredProperties.length }} of {{ properties.length }} properties · choose a property to see its rooms</div>

      <div class="property-list">
        @for(p of filteredProperties; track p.propertyId){
        <div class="property-row" [class.open]="expandedPropertyId === p.propertyId">
          <div class="property-lead">
            <span class="property-icon">▤</span>
            <div class="property-info">
              <span class="property-name">{{ p.name }}</span>
              <span class="property-meta">{{ p.district || p.state || p.code || 'Property' }}</span>
            </div>
          </div>
          <button type="button" class="ac-secondary-small-btn" (click)="viewRooms(p.propertyId)">
            {{ expandedPropertyId === p.propertyId ? 'Hide rooms' : 'View rooms' }}
          </button>
        </div>

        @if(expandedPropertyId === p.propertyId){
        <div class="rooms">
          @for(room of rooms; track room.roomId){
          <div class="room-card">
            <div class="room-head">
              <div>
                <span class="room-number">Room {{ room.roomNumber }}</span>
                <span class="room-meta">{{ room.roomType }} · {{ room.gender }} · cap {{ room.capacity }}</span>
              </div>
              <span class="room-status" [class.full]="roomStatus(room) === 'Full'">{{ roomStatus(room) }} · {{ freeCount(room) }} free</span>
            </div>
            <div class="beds">
              @for(bed of room.beds; track bed.bedNumber){
              <button
                type="button"
                class="bed"
                [class.taken]="bed.taken"
                [class.selected]="isSelected(room.roomId, bed.bedNumber)"
                [disabled]="bed.taken"
                (click)="toggleBed(room, bed)"
              >{{ bed.bedNumber }}</button>
              }
            </div>
            <div class="room-actions">
              <button type="button" class="ac-secondary-small-btn" [disabled]="freeCount(room) === 0" (click)="selectFull(room)">Full</button>
              <button type="button" class="ac-secondary-small-btn" [disabled]="selectedCount(room.roomId) === 0" (click)="clearRoom(room)">Clear</button>
              <button type="button" class="ac-primary-btn" [disabled]="selectedCount(room.roomId) === 0" (click)="placeRoom(room)">
                Place ({{ selectedCount(room.roomId) }})
              </button>
            </div>
          </div>
          } @empty { <p class="muted">No available rooms in this property.</p> }
        </div>
        }
        } @empty { <p class="muted">No properties found.</p> }
      </div>
    </section>
  </div>

  <!-- footer -->
  <div class="assign-footer">
    <span class="footer-summary">{{ placedBeds }} of {{ totalGuests }} guests · {{ assignedSummary.properties }} properties · {{ assignedSummary.rooms }} rooms</span>
    <div class="footer-actions">
      <button type="button" class="ac-secondary-btn" (click)="close()">Close</button>
    </div>
  </div>
</div>

<app-loader [isLoading]="isLoading"></app-loader>
